import { useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, ChevronRight, GanttChart } from 'lucide-react'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { WorkdayCategoryWeekTimeline } from '@/components/workdays/workday-category-week-timeline'
import {
  buildWorkDayCategoryTimelineRows,
  countTimelineEntriesByStatus,
  filterTimelineRowsByStatus,
  formatWeekRangeLabel,
  getWeekDays,
  getWeekStartMonday,
  type WorkDayCategoryTimelineEntry,
  toIsoDateKey,
} from '@/lib/workday-category-timeline'
import { getLocationReturnPath } from '@/lib/return-navigation'
import {
  formatDateForLocale,
  formatTimeForLocale,
  preferOrgLocalDateTime,
  useOrganizationDateLocale,
} from '@/lib/org-date-locale'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { withReturnTo } from '@/lib/return-navigation'
import {
  getWorkDayEntryStatusBadgeClassName,
  getWorkDayEntryStatusLabel,
  getWorkDayEntryStatusTimelineColor,
  type WorkDayEntryStatus,
} from '@/lib/workday-entry-status'
import type { Job, WorkDayEntry } from '@/types'

function parseWeekAnchorFromSearch(search: string) {
  const params = new URLSearchParams(search)
  const weekParam = String(params.get('week') || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekParam)) {
    return null
  }

  const parsed = new Date(`${weekParam}T12:00:00`)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return getWeekStartMonday(parsed)
}

function parseSelectedDayFromSearch(search: string) {
  const params = new URLSearchParams(search)
  const dayParam = String(params.get('day') || '').trim()
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dayParam)) {
    return null
  }
  return dayParam
}

function formatTimelineDateTime(value?: string | null, locale = 'en-US') {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }

  return `${formatDateForLocale(parsed, locale)} ${formatTimeForLocale(parsed, locale, '-')}`
}

const TIMELINE_STATUS_FILTER_ORDER: WorkDayEntryStatus[] = [
  'pending_acceptance',
  'scheduled',
  'completed',
  'in_progress',
  'accepted',
  'rejected',
  'schedule_declined',
  'draft',
  'logged',
]

const SCHEDULING_TABS = [
  { value: '/job/realtime', label: 'Real Time' },
  { value: '/jobs/scheduling', label: 'Schedule' },
  { value: '/timeline', label: 'Timeline' },
  { value: '/workdays', label: 'Work Days' },
] as const

export default function TimelinePage() {
  const location = useLocation()
  const navigate = useNavigate()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const locale = useOrganizationDateLocale(serviceInstanceId)
  const currentReturnPath = getLocationReturnPath(location)

  const [weekAnchor, setWeekAnchor] = useState(() => parseWeekAnchorFromSearch(location.search) ?? getWeekStartMonday(new Date()))
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(() => parseSelectedDayFromSearch(location.search))
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null)
  const [hiddenStatusFilters, setHiddenStatusFilters] = useState<Set<WorkDayEntryStatus>>(() => new Set())
  const suppressedDayFromSearchRef = useRef<string | null>(null)
  const lastSearchDayRef = useRef<string | null>(parseSelectedDayFromSearch(location.search))
  const pendingDaySearchSyncRef = useRef<string | null>(null)
  const stableTimelineRowsRef = useRef<ReturnType<typeof buildWorkDayCategoryTimelineRows>>([])
  const jobCacheRef = useRef<Map<string, Job>>(new Map())
  const weekDays = useMemo(() => getWeekDays(weekAnchor), [weekAnchor])
  const weekDateKeys = useMemo(() => weekDays.map(toIsoDateKey), [weekDays])
  const timelineMode = selectedDayKey ? 'day' : 'week'
  const weekRangeLabel = useMemo(() => formatWeekRangeLabel(weekAnchor, locale), [weekAnchor, locale])
  const activeSchedulingTab = useMemo(() => {
    const pathname = location.pathname
    if (pathname.startsWith('/job/realtime')) return '/job/realtime'
    if (pathname.startsWith('/workdays')) return '/workdays'
    if (pathname.startsWith('/jobs/scheduling') || pathname.startsWith('/job/scheduling') || pathname.startsWith('/job/schedule-workers')) {
      return '/jobs/scheduling'
    }
    return '/timeline'
  }, [location.pathname])

  const handleSchedulingTabNavigate = (nextPath: string) => {
    if (!nextPath || nextPath === activeSchedulingTab) {
      return
    }

    navigate(withServiceInstanceId(nextPath, serviceInstanceId))
  }

  const entriesQuery = useQuery({
    queryKey: ['workday-entries', 'timeline', serviceInstanceId, weekDateKeys[0], weekDateKeys[6]],
    queryFn: () =>
      assignmentsApi.getWorkDayEntries({
        date_from: weekDateKeys[0],
        date_to: weekDateKeys[6],
        page: 1,
        page_size: 500,
      }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })

  const entries = (entriesQuery.data?.results ?? []) as WorkDayEntry[]
  const jobIds = useMemo(
    () => [...new Set(entries.map((entry) => String(entry.job)).filter(Boolean))],
    [entries],
  )

  const jobQueries = useQueries({
    queries: jobIds.map((jobId) => ({
      queryKey: ['job', jobId, serviceInstanceId, 'timeline'],
      queryFn: () =>
        jobsApi.getJob(jobId, serviceInstanceId ? { service_instance_id: serviceInstanceId } : undefined),
      staleTime: 60_000,
      placeholderData: keepPreviousData,
      enabled: Boolean(jobId),
    })),
  })

  const jobById = useMemo(() => {
    jobQueries.forEach((query, index) => {
      if (query.data) {
        jobCacheRef.current.set(jobIds[index], query.data)
      }
    })

    const map = new Map<string, Job>(jobCacheRef.current)
    jobQueries.forEach((query, index) => {
      if (query.data) {
        map.set(jobIds[index], query.data)
      }
    })
    return map
  }, [jobIds, jobQueries])

  const timelineRows = useMemo(
    () => buildWorkDayCategoryTimelineRows(entries, jobById, weekDateKeys),
    [entries, jobById, weekDateKeys],
  )

  const activeWeekKey = weekDateKeys[0] ?? ''
  const isInitialLoading = entriesQuery.isPending && !entriesQuery.isPlaceholderData
  const isWeekTransitioning = Boolean(
    activeWeekKey && (entriesQuery.isPlaceholderData || entriesQuery.isFetching),
  )

  const displayTimelineRows = useMemo(() => {
    if (timelineRows.length > 0) {
      stableTimelineRowsRef.current = timelineRows
      return timelineRows
    }

    if (isWeekTransitioning && stableTimelineRowsRef.current.length > 0) {
      return stableTimelineRowsRef.current
    }

    return timelineRows
  }, [isWeekTransitioning, timelineRows])

  const statusCounts = useMemo(
    () => countTimelineEntriesByStatus(displayTimelineRows),
    [displayTimelineRows],
  )

  const visibleStatusFilters = useMemo(
    () => TIMELINE_STATUS_FILTER_ORDER.filter((status) => (statusCounts.get(status) ?? 0) > 0),
    [statusCounts],
  )

  const filteredTimelineRows = useMemo(
    () => filterTimelineRowsByStatus(displayTimelineRows, hiddenStatusFilters),
    [displayTimelineRows, hiddenStatusFilters],
  )

  const hasActiveStatusFilters = hiddenStatusFilters.size > 0

  useEffect(() => {
    if (!entriesQuery.isPlaceholderData && !entriesQuery.isFetching) {
      stableTimelineRowsRef.current = timelineRows
    }
  }, [entriesQuery.isFetching, entriesQuery.isPlaceholderData, timelineRows])

  useEffect(() => {
    setHiddenStatusFilters(new Set())
  }, [activeWeekKey])

  const toggleStatusFilter = (status: WorkDayEntryStatus) => {
    setHiddenStatusFilters((current) => {
      const next = new Set(current)
      if (next.has(status)) {
        next.delete(status)
      } else {
        next.add(status)
      }
      return next
    })
  }

  const selectedTimelineEntry = useMemo(() => {
    if (!selectedEntryId) {
      return null
    }

    for (const row of filteredTimelineRows) {
      for (const dayEntries of row.entriesByDate.values()) {
        const found = dayEntries.find((item) => String(item.entry.id) === selectedEntryId)
        if (found) {
          return found
        }
      }
    }

    return null
  }, [selectedEntryId, filteredTimelineRows])

  const totalEntries = useMemo(
    () => filteredTimelineRows.reduce(
      (count, row) => count + [...row.entriesByDate.values()].reduce((dayCount, items) => dayCount + items.length, 0),
      0,
    ),
    [filteredTimelineRows],
  )

  const totalEntriesForSelectedDay = useMemo(() => {
    if (!selectedDayKey) {
      return totalEntries
    }
    return filteredTimelineRows.reduce((count, row) => count + (row.entriesByDate.get(selectedDayKey)?.length || 0), 0)
  }, [selectedDayKey, filteredTimelineRows, totalEntries])

  const syncTimelineSearch = (nextWeekAnchor: Date, nextSelectedDayKey: string | null) => {
    const params = new URLSearchParams(location.search)
    const nextWeek = toIsoDateKey(nextWeekAnchor)
    const hasSameWeek = params.get('week') === nextWeek
    const hasSameDay = (params.get('day') || '') === (nextSelectedDayKey || '')
    if (hasSameWeek && hasSameDay) {
      return
    }

    params.set('week', nextWeek)
    if (nextSelectedDayKey) {
      params.set('day', nextSelectedDayKey)
    } else {
      params.delete('day')
    }

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true },
    )
  }

  useEffect(() => {
    const fromSearch = parseWeekAnchorFromSearch(location.search)
    if (!fromSearch) {
      return
    }

    if (toIsoDateKey(fromSearch) !== toIsoDateKey(weekAnchor)) {
      setWeekAnchor(fromSearch)
    }
  }, [location.search, weekAnchor])

  useEffect(() => {
    const fromSearch = parseSelectedDayFromSearch(location.search)
    const previousFromSearch = lastSearchDayRef.current
    lastSearchDayRef.current = fromSearch

    const pendingDaySync = pendingDaySearchSyncRef.current
    if (pendingDaySync !== null) {
      const normalizedFromSearch = fromSearch ?? ''
      if (normalizedFromSearch === pendingDaySync) {
        pendingDaySearchSyncRef.current = null
      } else {
        return
      }
    }

    if (!fromSearch && suppressedDayFromSearchRef.current) {
      suppressedDayFromSearchRef.current = null
    }

    if (
      fromSearch
      && selectedDayKey === null
      && suppressedDayFromSearchRef.current === fromSearch
    ) {
      return
    }

    if (fromSearch && weekDateKeys.includes(fromSearch)) {
      if (fromSearch !== selectedDayKey) {
        setSelectedDayKey(fromSearch)
      }
      return
    }

    // Only clear local day-mode when URL explicitly moved from a day to no-day.
    // This avoids one-frame flicker while local state updates before navigate() syncs search.
    if (!fromSearch && previousFromSearch && selectedDayKey) {
      setSelectedDayKey(null)
    }
  }, [location.search, selectedDayKey, weekDateKeys])

  useEffect(() => {
    if (!selectedDayKey) {
      return
    }
    if (!weekDateKeys.includes(selectedDayKey)) {
      const selectedDay = new Date(`${selectedDayKey}T12:00:00`)
      if (!Number.isNaN(selectedDay.getTime())) {
        const nextWeekAnchor = getWeekStartMonday(selectedDay)
        setWeekAnchor(nextWeekAnchor)
        syncTimelineSearch(nextWeekAnchor, selectedDayKey)
      }
    }
  }, [selectedDayKey, weekDateKeys])

  useEffect(() => {
    if (!selectedEntryId || isWeekTransitioning) {
      return
    }

    const stillExists = filteredTimelineRows.some((row) =>
      [...row.entriesByDate.values()].some((dayEntries) =>
        dayEntries.some((item) => String(item.entry.id) === selectedEntryId),
      ),
    )

    if (!stillExists) {
      setSelectedEntryId(null)
    }
  }, [filteredTimelineRows, isWeekTransitioning, selectedEntryId])

  const shiftWeek = (offset: number) => {
    if (timelineMode === 'day') {
      const baseDay = selectedDayKey
        ? new Date(`${selectedDayKey}T12:00:00`)
        : new Date()
      baseDay.setDate(baseDay.getDate() + offset)
      const nextWeekAnchor = getWeekStartMonday(baseDay)
      const nextDayKey = toIsoDateKey(baseDay)
      pendingDaySearchSyncRef.current = nextDayKey
      setWeekAnchor(nextWeekAnchor)
      setSelectedDayKey(nextDayKey)
      syncTimelineSearch(nextWeekAnchor, nextDayKey)
      return
    }

    const nextWeekAnchor = getWeekStartMonday((() => {
      const next = new Date(weekAnchor)
      next.setDate(weekAnchor.getDate() + offset * 7)
      return next
    })())
    setWeekAnchor(nextWeekAnchor)
    syncTimelineSearch(nextWeekAnchor, selectedDayKey)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#dfeae3] bg-[#f7fbf8] p-2">
        {SCHEDULING_TABS.map((tab) => {
          const isActive = activeSchedulingTab === tab.value
          return (
            <Button
              key={tab.value}
              type="button"
              variant="ghost"
              onClick={() => handleSchedulingTabNavigate(tab.value)}
              className={cn(
                'h-8 rounded-md px-3 text-xs font-semibold',
                isActive
                  ? 'bg-[#169b62] text-white hover:bg-[#118753] hover:text-white'
                  : 'text-[#355445] hover:bg-[#e8f4ee]'
              )}
            >
              {tab.label}
            </Button>
          )
        })}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Timeline</h1>
          <p className="text-sm text-muted-foreground">
            Weekly view of workday entries grouped by job category.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {timelineMode === 'day' ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                suppressedDayFromSearchRef.current = selectedDayKey
                pendingDaySearchSyncRef.current = ''
                setSelectedDayKey(null)
              }}
            >
              Back to week
            </Button>
          ) : null}
          <Button type="button" variant="outline" size="icon" onClick={() => shiftWeek(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              const today = new Date()
              const nextWeekAnchor = getWeekStartMonday(today)
              setWeekAnchor(nextWeekAnchor)
              if (timelineMode === 'day') {
                const nextDayKey = toIsoDateKey(today)
                pendingDaySearchSyncRef.current = nextDayKey
                setSelectedDayKey(nextDayKey)
                syncTimelineSearch(nextWeekAnchor, nextDayKey)
                return
              }
              syncTimelineSearch(nextWeekAnchor, selectedDayKey)
            }}
          >
            {timelineMode === 'day' ? 'Today' : 'This week'}
          </Button>
          <Button type="button" variant="outline" size="icon" onClick={() => shiftWeek(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border/70 bg-card/60 p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Filter by status
          </p>
          {hasActiveStatusFilters ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => setHiddenStatusFilters(new Set())}
            >
              Show all
            </Button>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {visibleStatusFilters.length > 0 ? visibleStatusFilters.map((status) => {
            const color = getWorkDayEntryStatusTimelineColor({ status })
            const isHidden = hiddenStatusFilters.has(status)
            const count = statusCounts.get(status) ?? 0

            return (
              <button
                key={status}
                type="button"
                aria-pressed={!isHidden}
                onClick={() => toggleStatusFilter(status)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition',
                  color.badge,
                  isHidden && 'opacity-45 saturate-50 line-through',
                  !isHidden && 'ring-1 ring-primary/25',
                )}
              >
                <span className={cn('h-2 w-2 rounded-full bg-gradient-to-br', color.accent)} aria-hidden="true" />
                {getWorkDayEntryStatusLabel(status)}
                <span className="tabular-nums opacity-80">({count})</span>
              </button>
            )
          }) : (
            <span className="text-xs text-muted-foreground">No timed entries in this week.</span>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <GanttChart className="h-5 w-5 text-primary" />
              Category timeline
            </CardTitle>
            <CardDescription>
              {timelineMode === 'day'
                ? `Day view · ${totalEntriesForSelectedDay} scheduled entr${totalEntriesForSelectedDay === 1 ? 'y' : 'ies'}`
                : `Week of ${weekRangeLabel} · ${totalEntries} scheduled entr${totalEntries === 1 ? 'y' : 'ies'} across ${filteredTimelineRows.length} categor${filteredTimelineRows.length === 1 ? 'y' : 'ies'}`}
              {hasActiveStatusFilters ? ' · status filters applied' : ''}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
            <WorkdayCategoryWeekTimeline
              weekDays={weekDays}
              rows={filteredTimelineRows}
              mode={timelineMode}
              selectedDayKey={selectedDayKey}
              locale={locale}
              serviceInstanceId={serviceInstanceId}
              returnPath={currentReturnPath}
              isLoading={isInitialLoading}
              isRefreshing={isWeekTransitioning && !isInitialLoading}
              selectedEntryId={selectedEntryId}
              onSelectDay={(dayKey) => {
                pendingDaySearchSyncRef.current = dayKey
                setSelectedDayKey(dayKey)
              }}
              onSelectEntry={(entry: WorkDayCategoryTimelineEntry) => {
                setSelectedEntryId(String(entry.entry.id))
              }}
            />

            <aside className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
              {selectedTimelineEntry ? (
                (() => {
                  const entry = selectedTimelineEntry.entry
                  const statusLabel = entry.status_label || getWorkDayEntryStatusLabel(entry.status)
                  const dateLabel = formatDateForLocale(new Date(`${selectedTimelineEntry.workDateKey}T12:00:00`), locale)
                  const plannedStart = preferOrgLocalDateTime(entry.planned_start, entry.planned_start_local)
                  const plannedEnd = preferOrgLocalDateTime(entry.planned_end, entry.planned_end_local)
                  const checkIn = preferOrgLocalDateTime(entry.check_in, entry.check_in_local)
                  const checkOut = preferOrgLocalDateTime(entry.check_out, entry.check_out_local)
                  const openHref = withServiceInstanceId(
                    withReturnTo(
                      `/workdays/${entry.id}/edit${entry.job ? `?job=${encodeURIComponent(String(entry.job))}` : ''}`,
                      currentReturnPath,
                    ),
                    serviceInstanceId,
                  )

                  return (
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Workday details</p>
                        <h3 className="text-sm font-semibold text-foreground">
                          {entry.job_title?.trim() || `Job #${entry.job}`}
                        </h3>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={cn('text-[10px]', getWorkDayEntryStatusBadgeClassName(entry.status))}>
                          {statusLabel}
                        </Badge>
                        <Badge variant="outline" className="border-slate-200 bg-white text-[10px] text-slate-700">
                          {dateLabel}
                        </Badge>
                      </div>

                      <dl className="space-y-2 text-xs">
                        <div>
                          <dt className="text-muted-foreground">Worker</dt>
                          <dd className="font-medium text-foreground">{entry.worker_name?.trim() || 'Worker'}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Skill</dt>
                          <dd className="font-medium text-foreground">{entry.worker_skill_label?.trim() || '-'}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Planned</dt>
                          <dd className="font-medium text-foreground">
                            {formatTimelineDateTime(plannedStart, locale) || '-'}
                            {' '}
                            {'to'}
                            {' '}
                            {formatTimelineDateTime(plannedEnd, locale) || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Check-in / out</dt>
                          <dd className="font-medium text-foreground">
                            {formatTimelineDateTime(checkIn, locale) || '-'}
                            {' '}
                            {'to'}
                            {' '}
                            {formatTimelineDateTime(checkOut, locale) || '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Hours</dt>
                          <dd className="font-medium text-foreground">{entry.hours ?? '-'}</dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground">Notes</dt>
                          <dd className="font-medium text-foreground">{entry.notes?.trim() || '-'}</dd>
                        </div>
                      </dl>

                      <Button asChild size="sm" className="w-full">
                        <Link to={openHref}>Open workday</Link>
                      </Button>
                    </div>
                  )
                })()
              ) : (
                <div className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white/70 px-4 text-center">
                  <p className="text-sm font-medium text-slate-700">Select a workday block</p>
                  <p className="mt-1 text-xs text-slate-500">Details will appear here and stay visible while browsing the week.</p>
                </div>
              )}
            </aside>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
